import type { BlockDocumentContent } from "@unruly-global/api-types";

/**
 * Strips Foundry :objectreference[...]{...} syntax from text.
 * These are a legacy artifact from LLM prompts and should not render.
 */
export function stripObjectReferences(text: string): string {
  return text.replace(/:objectreference\[.*?\]\{.*?\}/g, "").trim();
}

/**
 * Sanitizes caption strings — strips [object Object] that appears when
 * backend serializes non-string values (e.g. LocalDate objects) into captions.
 */
export function sanitizeCaption(
  caption: string | undefined,
): string | undefined {
  if (!caption) return caption;
  const cleaned = caption.replace(/\[object Object\]/g, "").trim();
  return cleaned || undefined;
}

/**
 * Parses a report's `content` string as block content JSON.
 * Only call this when the report is already known to be a block document
 * (i.e. version === "2.0").
 * Returns the parsed content, or `null` if the JSON is invalid.
 *
 * Handles double-escaped content from pipelines that stringify twice
 * (e.g. \\\" instead of \" and \\n instead of \n).
 */
export function parseBlockContent(
  content: string,
): BlockDocumentContent | null {
  // Try parsing as-is first, then retry with unescaped content.
  for (const attempt of [content, content.replace(/\\\\/g, "\\")]) {
    try {
      const parsed: unknown = JSON.parse(attempt);

      if (
        typeof parsed === "object" &&
        parsed !== null &&
        "blocks" in parsed &&
        Array.isArray((parsed as BlockDocumentContent).blocks)
      ) {
        const doc = parsed as BlockDocumentContent;
        // Backfill uid if the pipeline omits it — needed for React keys
        // and card-click scroll navigation.
        for (let i = 0; i < doc.blocks.length; i++) {
          if (!doc.blocks[i].uid) {
            doc.blocks[i].uid = `blk-auto-${i}`;
          }
        }
        return doc;
      }
    } catch {
      // Try next attempt
    }
  }

  return null;
}

/**
 * Collects all unique event IDs referenced across every block in a
 * Block Document. Checks both block-level `sources` and per-segment
 * `sources` in text blocks.
 */
export function collectBlockDocumentSources(
  doc: BlockDocumentContent,
): string[] {
  const ids = new Set<string>();
  for (const block of doc.blocks) {
    if ("sources" in block && Array.isArray(block.sources)) {
      for (const id of block.sources) {
        ids.add(id);
      }
    }
    if (block.type === "text" && block.segments) {
      for (const seg of block.segments) {
        if (seg.sources) {
          for (const id of seg.sources) {
            ids.add(id);
          }
        }
      }
    }
  }
  return Array.from(ids);
}
